import { useState, useRef } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, CheckCircle2, Loader2, Paperclip, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import SectionHeader from "../components/SectionHeader";
import { BUSINESS, SERVICES } from "../lib/business";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = {
  full_name: "",
  phone: "",
  email: "",
  service: SERVICES[0].title,
  city: "",
  preferred_date: "",
  chat_time: "",
  description: "",
  contact_method: "call",
};

export default function ContactPage() {
  const [form, setForm] = useState(INITIAL);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...incoming].slice(0, 5));
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || submitted) return;
    if (!form.full_name || !form.phone || !form.email || !form.description) {
      toast.error("Please fill in name, phone, email, and a short description.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      files.forEach((f) => fd.append("photos", f, f.name));
      await axios.post(`${API}/estimates`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
      toast.success("Estimate request sent! We'll be in touch shortly.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again, or call/text us directly at (227) 277-2112.", {
        duration: 8000,
        action: { label: "Call/Text", onClick: () => { window.location.href = BUSINESS.phoneHref; } },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Toaster position="top-center" richColors />
      <section className="bg-secondary/60 border-b border-border">
        <div className="container-x py-14 sm:py-20">
          <SectionHeader
            eyebrow="Get in Touch"
            title="Request a Free Estimate"
            subtitle="Tell us what you need help with. There is no commitment required."
          />
        </div>
      </section>

      <section className="section-y">
        <div className="container-x grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Contact info */}
          <aside className="lg:col-span-2 space-y-6">
            <div className="bg-primary text-white rounded-2xl p-7">
              <h3 className="text-xl font-bold">Contact directly</h3>
              <p className="text-white/80 mt-2 text-sm">
                Prefer a quick call or text? Reach us anytime.
              </p>
              <ul className="mt-6 space-y-4">
                <li>
                  <a href={BUSINESS.phoneHref} data-testid="contact-phone-link" className="flex items-center gap-3 text-white hover:text-accent transition-colors">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10">
                      <Phone size={18} />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-white/60">Call or Text</div>
                      <div className="font-semibold">{BUSINESS.phone}</div>
                    </div>
                  </a>
                </li>
                <li>
                  <a href={BUSINESS.emailHref} data-testid="contact-email-link" className="flex items-center gap-3 text-white hover:text-accent transition-colors">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10">
                      <Mail size={18} />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-white/60">Email</div>
                      <div className="font-semibold break-all">{BUSINESS.email}</div>
                    </div>
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 shrink-0">
                    <MapPin size={18} />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-white/60">Service Area</div>
                    <div className="text-sm text-white/90 mt-0.5">{BUSINESS.serviceArea}</div>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-white border border-border rounded-2xl p-7">
              <h4 className="font-bold text-primary">What happens next</h4>
              <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-accent mt-0.5" /> We respond quickly to confirm details.</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-accent mt-0.5" /> You'll get fair, honest pricing upfront.</li>
                <li className="flex gap-2"><CheckCircle2 size={16} className="text-accent mt-0.5" /> We schedule a time that works for you.</li>
              </ul>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div data-testid="estimate-success" className="bg-white border border-border rounded-2xl p-8 sm:p-10 text-center">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-primary">
                  <CheckCircle2 size={28} />
                </span>
                <h3 className="mt-5 text-2xl font-extrabold text-primary">
                  Thank you for requesting a free estimate.
                </h3>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                  We received your request and will contact you shortly by your preferred contact method.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                  <a href={BUSINESS.phoneHref} className="btn-gold" data-testid="success-call-btn">
                    <Phone size={18} /> Call or Text {BUSINESS.phone}
                  </a>
                  <button
                    onClick={() => { setSubmitted(false); setForm(INITIAL); setFiles([]); }}
                    className="btn-ghost-dark"
                    data-testid="success-new-btn"
                  >
                    Submit another request
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="estimate-form"
                className="bg-white border border-border rounded-2xl p-6 sm:p-8 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Full name" required>
                    <input
                      data-testid="input-full-name"
                      value={form.full_name}
                      onChange={onChange("full_name")}
                      required
                      className="input-base"
                      placeholder="Jane Doe"
                    />
                  </Field>
                  <Field label="Phone number" required>
                    <input
                      data-testid="input-phone"
                      value={form.phone}
                      onChange={onChange("phone")}
                      required
                      className="input-base"
                      placeholder="301-555-0199"
                    />
                  </Field>
                </div>
                <Field label="Email address" required>
                  <input
                    data-testid="input-email"
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    required
                    className="input-base"
                    placeholder="you@example.com"
                  />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Service needed" required>
                    <select
                      data-testid="input-service"
                      value={form.service}
                      onChange={onChange("service")}
                      className="input-base bg-white"
                      required
                    >
                      {SERVICES.map((s) => (
                        <option key={s.slug}>{s.title}</option>
                      ))}
                      <option>Other / Not listed</option>
                    </select>
                  </Field>
                  <Field label="City / location">
                    <input
                      data-testid="input-city"
                      value={form.city}
                      onChange={onChange("city")}
                      className="input-base"
                      placeholder="Gaithersburg, MD"
                    />
                  </Field>
                </div>
                <Field label="Preferred date or time">
                  <input
                    data-testid="input-preferred-date"
                    value={form.preferred_date}
                    onChange={onChange("preferred_date")}
                    className="input-base"
                    placeholder="e.g. Saturday afternoon"
                  />
                </Field>
                <Field label="Convenient time for a quick 5-minute call or text chat">
                  <textarea
                    data-testid="input-chat-time"
                    value={form.chat_time}
                    onChange={onChange("chat_time")}
                    rows={2}
                    className="input-base resize-none"
                    placeholder="Example: Today after 5 PM, tomorrow morning, weekdays after 6 PM, etc."
                  />
                </Field>
                <Field label="Project description" required>
                  <textarea
                    data-testid="input-description"
                    value={form.description}
                    onChange={onChange("description")}
                    rows={5}
                    required
                    className="input-base resize-none"
                    placeholder="A few details about what you need — TV size & wall type, furniture brand, items to move, etc."
                  />
                </Field>

                <Field label="Add photos (optional)">
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={onFiles}
                      className="hidden"
                      data-testid="input-photos"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-border bg-secondary/60 hover:bg-secondary text-sm font-semibold text-primary transition-colors"
                      data-testid="add-photos-btn"
                    >
                      <Paperclip size={16} /> Choose photos
                    </button>
                    {files.length > 0 && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Photos will be attached to the email we send to Abid Home Solutions. They are not stored on the website.
                      </p>
                    )}
                    {files.length > 0 && (
                      <ul className="mt-3 flex flex-wrap gap-2" data-testid="photo-preview-list">
                        {files.map((f, i) => (
                          <li key={i} className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-2.5 py-1.5 text-xs">
                            <Paperclip size={12} /> {f.name}
                            <button type="button" aria-label="remove" onClick={() => removeFile(i)} className="ml-1 text-muted-foreground hover:text-primary" data-testid={`remove-photo-${i}`}>
                              <X size={12} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Field>

                <Field label="Preferred contact method">
                  <div className="flex flex-wrap gap-2" data-testid="contact-method-group">
                    {[
                      { v: "call", label: "Call" },
                      { v: "text", label: "Text" },
                      { v: "email", label: "Email" },
                    ].map((opt) => (
                      <button
                        type="button"
                        key={opt.v}
                        onClick={() => setForm((f) => ({ ...f, contact_method: opt.v }))}
                        data-testid={`contact-method-${opt.v}`}
                        className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                          form.contact_method === opt.v
                            ? "bg-primary text-white border-primary"
                            : "bg-white text-primary border-border hover:bg-secondary"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-estimate-btn"
                  className="btn-gold w-full sm:w-auto disabled:opacity-70"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  {submitting ? "Sending..." : "Send Request"}
                </button>
                <p className="text-xs text-muted-foreground">
                  After you submit your request, we'll reach out for a quick 5-minute call or text chat to confirm the details, answer questions, and schedule your service.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-primary mb-1.5">
        {label} {required && <span className="text-accent">*</span>}
      </span>
      {children}
    </label>
  );
}
